import {apolloClient, gql} from '../graphql';
import {logAndThrowGraphQLErrors} from '../graphql/helpers';
import {DateTime} from 'luxon';
import {formatIsoDate} from 'shared/src/datetime';
import {keyBy} from 'shared/src/map';
import type {Project} from 'shared/src/models';

type NewProject = Omit<Project, 'id' | 'transactions' | 'tags' | 'files' | 'notes'>;

export const createProject = async (project: NewProject): Promise<Project> => {
	const {data, errors} = await apolloClient.mutate({
		mutation: gql(/* GraphQL */ `
			mutation CreateProject($newProject: NewProjectInput) {
				createProject(project: $newProject) {
					id
				}
			}
		`),
		variables: {
			newProject: {
				...project,
				customerId: project.customer,
				invoiceId: project.invoice,
				startDate: formatIsoDate(project.startDate),
				endDate: project.endDate ? formatIsoDate(project.endDate) : null,
			},
		},
	});

	if (errors && errors.length > 0) {
		logAndThrowGraphQLErrors(errors);
	}

	return {
		...project,
		id: data!.createProject.id,
		files: [],
		notes: [],
		tags: [],
		transactions: [],
	};
};

export const getProjects = async ({customerId}: {customerId?: string} = {}): Promise<Project[]> => {
	const {data, error} = await apolloClient.query({
		query: gql(/* GraphQL */ `
			query GetProjects {
				projects {
					id
					name
					description
					startDate
					endDate
					hoursLogged
					cost
					deposit
					status
					invoice {
						id
					}
					customer {
						id
					}
					transactions {
						id
					}
					tags {
						id
					}
					files {
						id
					}
					notes {
						id
					}
				}
			}
		`),
		fetchPolicy: 'no-cache',
	});

	if (error) {
		throw error;
	}

	let projects = data.projects;

	if (customerId) {
		projects = projects.filter((project) => project.customer.id === customerId);
	}

	return projects.map((project) => {
		return {
			id: project.id,
			name: project.name,
			description: project.description,
			startDate: DateTime.fromISO(project.startDate),
			endDate: project.endDate ? DateTime.fromISO(project.endDate) : null,
			hoursLogged: project.hoursLogged ?? null,
			cost: project.cost ?? null,
			deposit: project.deposit ?? null,
			status: project.status,
			invoice: project.invoice?.id ?? null,
			customer: project.customer.id,
			transactions: project.transactions.map((transaction) => transaction.id),
			tags: project.tags.map((tag) => tag.id),
			files: project.files.map((file) => file.id),
			notes: project.notes.map((note) => note.id),
		};
	});
};

export const getProjectMap = async (): Promise<Map<string, Project>> => {
	return keyBy(await getProjects(), (project) => project.id);
};

export const getProject = async (id: string): Promise<Project> => {
	const project = (await getProjectMap()).get(id);

	if (!project) {
		throw new Error(`Project ${id} not found`);
	}

	return project;
};

export const updateProject = async (project: Project): Promise<Project> => {
	const {errors} = await apolloClient.mutate({
		mutation: gql(/* GraphQL */ `
			mutation UpdateProject($project: UpdatedProjectInput!) {
				updateProject(project: $project) {
					id
				}
			}
		`),
		variables: {
			project: {
				id: project.id,
				name: project.name,
				description: project.description,
				startDate: formatIsoDate(project.startDate),
				endDate: project.endDate ? formatIsoDate(project.endDate) : null,
				cost: project.cost,
				deposit: project.deposit,
				hoursLogged: project.hoursLogged,
				status: project.status,
				customerId: project.customer,
				invoiceId: project.invoice,
			},
		},
	});

	if (errors && errors.length > 0) {
		logAndThrowGraphQLErrors(errors);
	}

	return project;
};
